import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert,
  Chip,
  CircularProgress,
  Divider,
} from "@mui/material";
import TopNavBar from "../components/TopNavBar";
import { getConnectionStatus } from "../api/telemetry";

function formatTimestamp(value) {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString();
}

function formatDuration(ms) {
  if (typeof ms !== "number") {
    return "-";
  }

  if (ms < 1000) {
    return `${ms} ms`;
  }

  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes === 0) {
    return `${seconds}s`;
  }

  return `${minutes}m ${seconds}s`;
}

function getSseStatus(metrics, sseOpen) {
  if (!sseOpen) {
    return { label: "Offline", color: "default" };
  }

  if (!metrics) {
    return { label: "Open", color: "info" };
  }

  if (metrics.isActive === false) {
    return { label: "Stale", color: "warning" };
  }

  if (typeof metrics.lastHeartbeatTime === "string") {
    return { label: "Heartbeat", color: "success" };
  }

  if (typeof metrics.lastEventTime === "string") {
    return { label: "Receiving Events", color: "success" };
  }

  return { label: "Quiet", color: "info" };
}

function buildDeviceRows(groups = {}) {
  if (!groups || typeof groups !== "object") {
    return [];
  }

  return Object.entries(groups).flatMap(([groupName, devices]) => {
    const safeDevices = Array.isArray(devices) ? devices : [];
    return safeDevices.map((device) => ({
      ...device,
      pollingGroup: groupName,
    }));
  });
}

function normalizeText(value) {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
}

function isIdLikeLabel(label, candidates = []) {
  const normalizedLabel = normalizeText(label);
  if (!normalizedLabel) {
    return false;
  }

  const lowerLabel = normalizedLabel.toLowerCase();
  return candidates
    .map((value) => normalizeText(value))
    .filter(Boolean)
    .some((value) => value.toLowerCase() === lowerLabel);
}

function getPreferredClientLabel(...sources) {
  const normalizedSources = sources
    .map((source) => ({
      name: normalizeText(source?.name),
      id: normalizeText(source?.id),
      pk: normalizeText(source?.pk),
    }))
    .filter((source) => source.name || source.id || source.pk);

  for (const source of normalizedSources) {
    if (source.name && !isIdLikeLabel(source.name, [source.id, source.pk])) {
      return source.name;
    }
  }

  for (const source of normalizedSources) {
    if (source.name) {
      return source.name;
    }
  }

  for (const source of normalizedSources) {
    if (source.id) {
      return source.id;
    }
  }

  return "-";
}

function getPreferredDeviceName({ metricName, fallbackName, deviceId }) {
  const normalizedMetricName = normalizeText(metricName);
  const normalizedFallbackName = normalizeText(fallbackName);
  const normalizedDeviceId = normalizeText(deviceId);

  if (
    normalizedMetricName &&
    !isIdLikeLabel(normalizedMetricName, [normalizedDeviceId])
  ) {
    return normalizedMetricName;
  }

  if (normalizedFallbackName) {
    return normalizedFallbackName;
  }

  return normalizedMetricName || "-";
}

function SummaryCard({ label, value, subtext }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        minWidth: 160,
        flex: "1 1 180px",
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
        {value}
      </Typography>
      {subtext ? (
        <Typography variant="caption" color="text.secondary">
          {subtext}
        </Typography>
      ) : null}
    </Paper>
  );
}

export default function DeviceConnectionStatusPage() {
  const [cachedConnectionData, setCachedConnectionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCachedConnectionData = async () => {
    setLoading(true);
    setError(null);

    const result = await getConnectionStatus();

    if (result.success) {
      setCachedConnectionData(result.data);
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchCachedConnectionData();
  }, []);

  const pollingSnapshot =
    cachedConnectionData &&
    cachedConnectionData.devices &&
    typeof cachedConnectionData.devices === "object" &&
    !Array.isArray(cachedConnectionData.devices)
      ? cachedConnectionData.devices
      : cachedConnectionData || null;
  const pollingServiceStatus =
    cachedConnectionData?.pollingServiceStatus ||
    cachedConnectionData?.status ||
    "-";
  const groupedDevices = pollingSnapshot?.devices || {};
  const deviceRows = buildDeviceRows(groupedDevices);
  const connectedRobots = pollingSnapshot?.connectedRobots || [];
  const counts = pollingSnapshot?.counts || {};
  const openConnections = pollingSnapshot?.openConnections || {};
  const sseActivitySummary = pollingSnapshot?.sseActivitySummary || {};
  const boschSSEConnections = pollingSnapshot?.boschSSEConnections || {};
  const boschConnectionRows = Object.entries(boschSSEConnections).map(
    ([connectionKey, stats]) => ({
      connectionKey,
      ...(stats || {}),
    }),
  );

  return (
    <>
      <TopNavBar heading={"Device Connection Status"} />
      <Container maxWidth="xl" sx={{ mt: 3, pb: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 4,
            border: "1px solid",
            borderColor: "divider",
            background:
              "linear-gradient(135deg, rgba(6,95,70,0.08), rgba(15,23,42,0.02))",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Cached Devices from Polling Service
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Live snapshot of device polling groups, open channels, and
                recent SSE activity.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Chip
                label={`Polling service ${pollingServiceStatus}`}
                color="primary"
                variant="outlined"
              />
              <Button
                variant="contained"
                onClick={fetchCachedConnectionData}
                disabled={loading}
              >
                Refresh
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              mt: 3,
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <SummaryCard
              label="Total Devices"
              value={pollingSnapshot?.total ?? 0}
            />
            <SummaryCard
              label="Open SSE"
              value={openConnections.sse ?? 0}
              subtext={`${sseActivitySummary.activeBoschConnections ?? 0} active`}
            />
            <SummaryCard
              label="Open WebSockets"
              value={openConnections.websocket ?? 0}
            />
            <SummaryCard
              label="Tracked SSE"
              value={sseActivitySummary.trackedBoschConnections ?? 0}
              subtext={`${sseActivitySummary.staleConnectionsByProtocolTimeout ?? 0} stale`}
            />
            <SummaryCard
              label="HTTP Connections"
              value={openConnections.http ?? 0}
            />
          </Box>
        </Paper>

        {loading ? (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              mb: 3,
              borderRadius: 4,
              border: "1px solid",
              borderColor: "divider",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Paper>
        ) : null}

        {error ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        ) : null}

        {pollingSnapshot ? (
          <>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                borderRadius: 4,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                  mb: 2,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Snapshot Overview
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Generated {formatTimestamp(pollingSnapshot.generatedAt)}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                <Chip label={`Normal ${counts.normal ?? 0}`} />
                <Chip label={`Fast ${counts.fast ?? 0}`} />
                <Chip label={`Bosch ${counts.bosch ?? 0}`} />
                <Chip
                  label={`Recent Events ${sseActivitySummary.devicesWithRecentEvents ?? 0}`}
                  color="success"
                  variant="outlined"
                />
                <Chip
                  label={`Heartbeats ${sseActivitySummary.connectionsWithRecentHeartbeats ?? 0}`}
                  color="info"
                  variant="outlined"
                />
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Typography variant="body2" color="text.secondary">
                This view separates channel health from event traffic. A device
                can have a healthy open SSE connection while showing no recent
                business events.
              </Typography>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                borderRadius: 4,
                border: "1px solid",
                borderColor: "divider",
                overflowX: "auto",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Connected Robots
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Device ID</TableCell>
                    <TableCell>Device Name</TableCell>
                    <TableCell>Manufacturer</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>SSE</TableCell>
                    <TableCell>WebSocket</TableCell>
                    <TableCell>Connection Key</TableCell>
                    <TableCell>Protocol Idle</TableCell>
                    <TableCell>Event Idle</TableCell>
                    <TableCell>Last Event</TableCell>
                    <TableCell>Last Disconnect</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {connectedRobots.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11}>
                        <Typography variant="body2" color="text.secondary">
                          No active SSE or WebSocket devices reported.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    connectedRobots.map((device) => {
                      const sseStatus = getSseStatus(
                        device.sseMetrics,
                        device.sse,
                      );

                      return (
                        <TableRow key={device.uk_device_id} hover>
                          <TableCell>{device.uk_device_id}</TableCell>
                          <TableCell>
                            {getPreferredDeviceName({
                              metricName: device.sseMetrics?.device_name,
                              fallbackName: device.name,
                              deviceId: device.uk_device_id,
                            })}
                          </TableCell>
                          <TableCell sx={{ textTransform: "capitalize" }}>
                            {device.manufacturer || "-"}
                          </TableCell>
                          <TableCell>
                            {getPreferredClientLabel(
                              {
                                name: device.sseMetrics?.client_name,
                                id: device.sseMetrics?.client_id,
                                pk: device.sseMetrics?.client_pk_id,
                              },
                              {
                                name: device.assignedClient?.client_name,
                                id: device.assignedClient?.client_id,
                                pk:
                                  device.assignedClient?.client_pk_id ||
                                  device.assignedClient?.pk_id,
                              },
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={sseStatus.label}
                              color={sseStatus.color}
                              variant={device.sse ? "filled" : "outlined"}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={device.websocket ? "Open" : "Closed"}
                              color={device.websocket ? "success" : "default"}
                              variant={device.websocket ? "filled" : "outlined"}
                            />
                          </TableCell>
                          <TableCell>
                            {device.sseMetrics?.connectionKey || "-"}
                          </TableCell>
                          <TableCell>
                            {formatDuration(device.sseMetrics?.protocolIdleMs)}
                          </TableCell>
                          <TableCell>
                            {formatDuration(
                              device.sseMetrics?.deviceEventIdleMs ??
                                device.sseMetrics?.eventIdleMs,
                            )}
                          </TableCell>
                          <TableCell>
                            {formatTimestamp(
                              device.sseMetrics?.deviceLastEventAt ??
                                device.sseMetrics?.lastEventTime,
                            )}
                          </TableCell>
                          <TableCell>
                            {device.sseMetrics?.lastDisconnectReason || "-"}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 4,
                border: "1px solid",
                borderColor: "divider",
                overflowX: "auto",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                All Cached Devices
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Device ID</TableCell>
                    <TableCell>Device Name</TableCell>
                    <TableCell>Group</TableCell>
                    <TableCell>Manufacturer</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>SSE Status</TableCell>
                    <TableCell>WebSocket</TableCell>
                    <TableCell>Connection Key</TableCell>
                    <TableCell>Heartbeat</TableCell>
                    <TableCell>Protocol Idle</TableCell>
                    <TableCell>Event Idle</TableCell>
                    <TableCell>Last Activity</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deviceRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11}>
                        <Typography variant="body2" color="text.secondary">
                          No cached devices available.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    deviceRows.map((device) => {
                      const sseStatus = getSseStatus(
                        device.liveConnection?.sseMetrics,
                        device.liveConnection?.sse,
                      );

                      return (
                        <TableRow
                          key={`${device.pollingGroup}-${device.uk_device_id}`}
                          hover
                        >
                          <TableCell>{device.uk_device_id}</TableCell>
                          <TableCell>
                            {getPreferredDeviceName({
                              metricName:
                                device.liveConnection?.sseMetrics?.device_name,
                              fallbackName: device.name,
                              deviceId: device.uk_device_id,
                            })}
                          </TableCell>
                          <TableCell sx={{ textTransform: "capitalize" }}>
                            {device.pollingGroup}
                          </TableCell>
                          <TableCell sx={{ textTransform: "capitalize" }}>
                            {device.manufacturer || "-"}
                          </TableCell>
                          <TableCell>
                            {getPreferredClientLabel(
                              {
                                name: device.liveConnection?.sseMetrics
                                  ?.client_name,
                                id: device.liveConnection?.sseMetrics
                                  ?.client_id,
                                pk: device.liveConnection?.sseMetrics
                                  ?.client_pk_id,
                              },
                              {
                                name: device.assignedClient?.client_name,
                                id: device.assignedClient?.client_id,
                                pk:
                                  device.assignedClient?.client_pk_id ||
                                  device.assignedClient?.pk_id,
                              },
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={sseStatus.label}
                              color={sseStatus.color}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={
                                device.liveConnection?.websocket
                                  ? "Open"
                                  : "Closed"
                              }
                              color={
                                device.liveConnection?.websocket
                                  ? "success"
                                  : "default"
                              }
                              variant={
                                device.liveConnection?.websocket
                                  ? "filled"
                                  : "outlined"
                              }
                            />
                          </TableCell>
                          <TableCell>
                            {device.liveConnection?.sseMetrics?.connectionKey ||
                              "-"}
                          </TableCell>
                          <TableCell>
                            {formatTimestamp(
                              device.liveConnection?.sseMetrics
                                ?.lastHeartbeatTime,
                            )}
                          </TableCell>
                          <TableCell>
                            {formatDuration(
                              device.liveConnection?.sseMetrics?.protocolIdleMs,
                            )}
                          </TableCell>
                          <TableCell>
                            {formatDuration(
                              device.liveConnection?.sseMetrics
                                ?.deviceEventIdleMs ??
                                device.liveConnection?.sseMetrics?.eventIdleMs,
                            )}
                          </TableCell>
                          <TableCell>
                            {formatTimestamp(
                              device.liveConnection?.sseMetrics
                                ?.deviceLastEventAt ??
                                device.liveConnection?.sseMetrics
                                  ?.lastProtocolActivityTime,
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 3,
                mt: 3,
                borderRadius: 4,
                border: "1px solid",
                borderColor: "divider",
                overflowX: "auto",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Bosch Shared SSE Connections
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Connection Key</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Connected At</TableCell>
                    <TableCell>Last Event</TableCell>
                    <TableCell>Last Heartbeat</TableCell>
                    <TableCell>Protocol Idle</TableCell>
                    <TableCell>Reconnect Attempts</TableCell>
                    <TableCell>Last Disconnect</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {boschConnectionRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Typography variant="body2" color="text.secondary">
                          No Bosch SSE connection entries returned.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    boschConnectionRows.map((connection) => (
                      <TableRow key={connection.connectionKey} hover>
                        <TableCell>{connection.connectionKey}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={connection.isActive ? "Active" : "Inactive"}
                            color={connection.isActive ? "success" : "default"}
                            variant={
                              connection.isActive ? "filled" : "outlined"
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {formatTimestamp(connection.connectedAt)}
                        </TableCell>
                        <TableCell>
                          {formatTimestamp(connection.lastEventTime)}
                        </TableCell>
                        <TableCell>
                          {formatTimestamp(connection.lastHeartbeatTime)}
                        </TableCell>
                        <TableCell>
                          {formatDuration(connection.protocolIdleMs)}
                        </TableCell>
                        <TableCell>
                          {connection.reconnectAttempts ?? 0}
                        </TableCell>
                        <TableCell>
                          {connection.lastDisconnectReason || "-"}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Paper>
          </>
        ) : null}
      </Container>
    </>
  );
}
